import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Button,
  FileButton,
  Group,
  Loader,
  Progress,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { ExternalLink, Music, Trash2, Upload } from 'lucide-react';
import {
  createUserCustomAudios,
  deleteUserCustomAudio,
  fetchUserCustomAudios,
  presignUserCustomAudio,
  uploadToPresignedUrl,
  type UserCustomAudioGroup,
} from '../../services/userAudiosManager';

type UserCustomAudiosTabProps = {
  userId: string;
};

const LANG_OPTIONS = [
  { value: 'it', label: 'Italiano (it)' },
  { value: 'en', label: 'English (en)' },
  { value: 'es', label: 'Español (es)' },
  { value: 'fr', label: 'Français (fr)' },
  { value: 'de', label: 'Deutsch (de)' },
];

const TYPE_OPTIONS = [
  { value: 'default', label: 'Default' },
  { value: 'intro', label: 'Intro' },
  { value: 'outro', label: 'Outro' },
];

function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

export const UserCustomAudiosTab = memo(function UserCustomAudiosTab({
  userId,
}: UserCustomAudiosTabProps) {
  const navigate = useNavigate();
  const [audios, setAudios] = useState<UserCustomAudioGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadPercent, setUploadPercent] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const [name, setName] = useState('');
  const [lang, setLang] = useState<string | null>('it');
  const [audioType, setAudioType] = useState<string | null>('default');
  const [file, setFile] = useState<File | null>(null);

  const loadAudios = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await fetchUserCustomAudios(userId);
      setAudios(rows);
    } catch (error) {
      notifications.show({
        color: 'red',
        title: 'Errore',
        message:
          error instanceof Error
            ? error.message
            : 'Impossibile caricare gli audio personalizzati',
      });
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void loadAudios();
  }, [loadAudios]);

  const canSubmit = useMemo(
    () =>
      Boolean(file) &&
      name.trim().length > 0 &&
      Boolean(lang) &&
      Boolean(audioType) &&
      !saving,
    [file, name, lang, audioType, saving]
  );

  const handleSubmit = useCallback(async () => {
    if (!file || !lang || !audioType) return;
    setSaving(true);
    setUploadPercent(0);
    try {
      const contentType = file.type || 'audio/mpeg';
      const presign = await presignUserCustomAudio(
        userId,
        file.name,
        contentType
      );
      await uploadToPresignedUrl(
        presign.putUrl,
        file,
        contentType,
        progress => {
          setUploadPercent(progress.percent);
        }
      );
      setUploadPercent(100);
      const group = await createUserCustomAudios(userId, {
        name: name.trim(),
        lang,
        type: audioType,
        url: presign.publicUrl,
        videoIds: [],
        size: file.size,
      });
      notifications.show({
        color: 'green',
        title: 'Audio caricato',
        message: 'Apri il dettaglio per collegare i video',
      });
      navigate(`/admin/users/${userId}/audios/${group.id}`);
    } catch (error) {
      notifications.show({
        color: 'red',
        title: 'Upload fallito',
        message: error instanceof Error ? error.message : 'Errore sconosciuto',
      });
    } finally {
      setSaving(false);
      setUploadPercent(null);
    }
  }, [audioType, file, lang, name, navigate, userId]);

  const handleDelete = useCallback(
    async (audioId: number) => {
      setDeletingId(audioId);
      try {
        await deleteUserCustomAudio(userId, audioId);
        notifications.show({
          color: 'green',
          title: 'Eliminato',
          message: 'Gruppo audio personalizzato rimosso',
        });
        await loadAudios();
      } catch (error) {
        notifications.show({
          color: 'red',
          title: 'Eliminazione fallita',
          message:
            error instanceof Error ? error.message : 'Errore sconosciuto',
        });
      } finally {
        setDeletingId(null);
      }
    },
    [loadAudios, userId]
  );

  return (
    <Stack gap='lg'>
      <Title order={3}>Gestione audio personalizzati</Title>

      <Stack
        gap='md'
        p='md'
        style={{
          border: '1px solid var(--mantine-color-gray-3)',
          borderRadius: 8,
        }}
      >
        <Text fw={600}>Carica nuovo audio</Text>
        <Text size='sm' c='dimmed'>
          Dopo l&apos;upload potrai collegare i video dalla pagina di dettaglio.
        </Text>
        <TextInput
          label='Nome'
          placeholder='Es. Narratore personalizzato'
          value={name}
          onChange={event => setName(event.currentTarget.value)}
          required
        />
        <Select
          label='Lingua'
          data={LANG_OPTIONS}
          value={lang}
          onChange={setLang}
          required
        />
        <Select
          label='Tipo'
          data={TYPE_OPTIONS}
          value={audioType}
          onChange={setAudioType}
          allowDeselect={false}
          required
        />
        <Group align='flex-end'>
          <FileButton
            onChange={setFile}
            accept='audio/*,.mp3,.wav,.m4a,.ogg,.aac'
          >
            {props => (
              <Button
                {...props}
                variant='light'
                leftSection={<Music size={16} />}
              >
                {file ? file.name : 'Scegli file audio'}
              </Button>
            )}
          </FileButton>
          <Button
            leftSection={<Upload size={16} />}
            disabled={!canSubmit}
            loading={saving}
            onClick={() => void handleSubmit()}
          >
            Carica audio
          </Button>
        </Group>
        {uploadPercent != null && (
          <Stack gap={4}>
            <Group justify='space-between'>
              <Text size='sm'>Upload su S3</Text>
              <Text size='sm' fw={600}>
                {uploadPercent}%
              </Text>
            </Group>
            <Progress value={uploadPercent} animated={uploadPercent < 100} />
          </Stack>
        )}
      </Stack>

      {loading ? (
        <Group justify='center' py='xl'>
          <Loader />
        </Group>
      ) : audios.length === 0 ? (
        <Text c='dimmed'>Nessun audio personalizzato per questo utente.</Text>
      ) : (
        <Table striped highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Nome</Table.Th>
              <Table.Th>Lingua</Table.Th>
              <Table.Th>Tipo</Table.Th>
              <Table.Th>Video collegati</Table.Th>
              <Table.Th>Data</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {audios.map(audio => (
              <Table.Tr key={audio.id}>
                <Table.Td>
                  <Text
                    component={Link}
                    to={`/admin/users/${userId}/audios/${audio.id}`}
                    style={{ textDecoration: 'none' }}
                    c='blue'
                    fw={500}
                  >
                    {audio.name}
                  </Text>
                </Table.Td>
                <Table.Td>{audio.lang}</Table.Td>
                <Table.Td>{audio.type || 'default'}</Table.Td>
                <Table.Td>
                  {audio.videos.length === 0
                    ? '—'
                    : audio.videos.map(v => v.name).join(', ')}
                </Table.Td>
                <Table.Td>{formatDate(audio.create_time)}</Table.Td>
                <Table.Td>
                  <Group gap='xs' wrap='nowrap'>
                    <Button
                      size='xs'
                      variant='light'
                      leftSection={<ExternalLink size={14} />}
                      component={Link}
                      to={`/admin/users/${userId}/audios/${audio.id}`}
                    >
                      Apri
                    </Button>
                    <Button
                      size='xs'
                      color='red'
                      variant='light'
                      leftSection={<Trash2 size={14} />}
                      loading={deletingId === audio.id}
                      onClick={() => void handleDelete(audio.id)}
                    >
                      Elimina
                    </Button>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
});
